import { PlacementChecklist, PlacementSpotId } from '@/types/activation/activation-local';

/**
 * Browser-local activation state. Three things live here and nowhere else:
 *
 * 1. the pack the customer last opened (so Activation opens it again),
 * 2. whether a pack's reveal has already played,
 * 3. the lookbook placement checklist (deliberately client-side by design).
 *
 * Same storage discipline as `emailCampaignId.ts`: every access is guarded and
 * falls back to an in-memory map, so a browser with storage disabled degrades
 * to "this session only" instead of throwing.
 */

const STORAGE_PREFIX = 'helpgenie:activation:';
const LAST_PACK_KEY = `${STORAGE_PREFIX}last-pack`;
const REVEALED_KEY = `${STORAGE_PREFIX}revealed`;
const PLACEMENTS_KEY = `${STORAGE_PREFIX}placements`;

const inMemory = new Map<string, string>();

const storage = (): Storage | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

const read = (key: string): string | null => {
  try {
    return storage()?.getItem(key) ?? inMemory.get(key) ?? null;
  } catch {
    return inMemory.get(key) ?? null;
  }
};

const write = (key: string, value: string): void => {
  inMemory.set(key, value);
  try {
    storage()?.setItem(key, value);
  } catch {
    // In-memory keeps this session consistent even without browser storage.
  }
};

const readJson = <T>(key: string, fallback: T): T => {
  const raw = read(key);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

// =====================================================================
// Last opened pack
// =====================================================================

interface LastPackPointer {
  packId: string;
  at: string;
}

/** The pack id the customer last opened, or null when there isn't one. */
export const getLastActivationPackId = (): string | null => {
  const pointer = readJson<LastPackPointer | null>(LAST_PACK_KEY, null);
  return typeof pointer?.packId === 'string' && pointer.packId ? pointer.packId : null;
};

export const rememberActivationPack = (packId: string): void => {
  if (!packId) return;
  write(LAST_PACK_KEY, JSON.stringify({ packId, at: new Date().toISOString() }));
};

// =====================================================================
// Reveal — once per pack
// =====================================================================

export const hasRevealedPack = (packId: string): boolean => {
  if (!packId) return true;
  const revealed = readJson<Record<string, boolean>>(REVEALED_KEY, {});
  return revealed[packId] === true;
};

export const markPackRevealed = (packId: string): void => {
  if (!packId) return;
  const revealed = readJson<Record<string, boolean>>(REVEALED_KEY, {});
  if (revealed[packId] === true) return;
  write(REVEALED_KEY, JSON.stringify({ ...revealed, [packId]: true }));
};

// =====================================================================
// Placement checklist
// =====================================================================

export const getPlacementChecklist = (packId: string): PlacementChecklist => {
  if (!packId) return {};
  const all = readJson<Record<string, PlacementChecklist>>(PLACEMENTS_KEY, {});
  return all[packId] || {};
};

export const setPlacementChecked = (
  packId: string,
  spot: PlacementSpotId,
  checked: boolean,
): PlacementChecklist => {
  const all = readJson<Record<string, PlacementChecklist>>(PLACEMENTS_KEY, {});
  const next: PlacementChecklist = { ...(all[packId] || {}), [spot]: checked };
  write(PLACEMENTS_KEY, JSON.stringify({ ...all, [packId]: next }));
  return next;
};
